import {
  TIME_UNIT_SECOND,
  TIME_UNIT_DAY,
  TIME_UNIT_MONTH,
  TIME_UNIT_YEAR,
} from "./timeLevels";

const FALLBACK_LOCALE = "en";
const MS_PER_MINUTE = 60 * 1000;
const MS_PER_DAY = 24 * 60 * MS_PER_MINUTE;
const SHORT_YEAR_DIGITS = 2;

const pad = (value) => String(value).padStart(2, "0");

const createMonthFormat = (locale) => {
  try {
    return new Intl.DateTimeFormat(locale || FALLBACK_LOCALE, { month: "short", timeZone: "UTC" });
  } catch (_error) {
    return new Intl.DateTimeFormat(FALLBACK_LOCALE, { month: "short", timeZone: "UTC" });
  }
};

/**
 * Formats time-axis labels by the unit of the period the bar starts
 * (spec §12.3 and references 07–09): `2026`, `Oct`, `28`, `12:33`, `12:33:30`.
 *
 * @param {string|null} locale - month names follow the interface language
 * @returns {(localMs: number, unit: string) => string} localMs from createZonedTime
 */
export const createTimeLabelFormatter = (locale) => {
  const monthFormat = createMonthFormat(locale);

  return (localMs, unit) => {
    const date = new Date(localMs);

    if (unit === TIME_UNIT_YEAR) return String(date.getUTCFullYear());
    if (unit === TIME_UNIT_MONTH) return monthFormat.format(date);
    if (unit === TIME_UNIT_DAY) return String(date.getUTCDate());

    const time = `${pad(date.getUTCHours())}:${pad(date.getUTCMinutes())}`;

    return unit === TIME_UNIT_SECOND ? `${time}:${pad(date.getUTCSeconds())}` : time;
  };
};

/**
 * Formats the time under the crosshair as on the references 02, 07–09:
 * `29 Oct '26  11:19`, with seconds on seconds timeframes
 * (`29 Oct '26  12:29:45`) and the date alone for daily and longer bars
 * (`15 Sep '26`).
 *
 * @param {string|null} locale - month names follow the interface language
 * @returns {(localMs: number, barDurationMs: number) => string} localMs from createZonedTime
 */
export const createCrosshairTimeFormatter = (locale) => {
  const monthFormat = createMonthFormat(locale);

  return (localMs, barDurationMs) => {
    const date = new Date(localMs);
    const year = String(date.getUTCFullYear()).slice(-SHORT_YEAR_DIGITS);
    const day = `${date.getUTCDate()} ${monthFormat.format(date)} '${year}`;

    if (barDurationMs >= MS_PER_DAY) return day;

    const time = `${pad(date.getUTCHours())}:${pad(date.getUTCMinutes())}`;
    const fullTime = barDurationMs < MS_PER_MINUTE ? `${time}:${pad(date.getUTCSeconds())}` : time;

    return `${day}  ${fullTime}`;
  };
};

// Widest label of each unit, used to decide how dense the labels can be
export const LABEL_SAMPLES = {
  [TIME_UNIT_SECOND]: "00:00:00",
  [TIME_UNIT_DAY]: "00",
  [TIME_UNIT_MONTH]: "May",
  [TIME_UNIT_YEAR]: "0000",
};
export const DEFAULT_LABEL_SAMPLE = "00:00";
